import React, { useEffect, useState } from 'react';
import { 
    Container, 
    Paper, 
    Typography, 
    Button,
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    Chip,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    IconButton,
    Tooltip
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import { InlineDatePicker } from '../../components/InlineDatePicker';
import { 
    getQuestionnaireResponsesByReviewEntity, 
    createQuestionnaireResponse, 
    getUsers,
    getReviewEntity,
    getReviewDefinitions,
    QuestionnaireResponse,
    QuestionnaireResponsePayload,
    ReviewDefinition
} from '../../services/api';
import { getUser } from '../../services/auth';
import { formatDisplayDate } from '../../utils/dateUtils';
import { UserUtils } from '../../types';
import { Add, ArrowBack } from '@mui/icons-material';

import { Assignment } from '@mui/icons-material';

const formatDateForAPI = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
};

interface User {
    id: number;
    user_name: string;
    display_name?: string;
    roles: Array<{
        role_id: number;
        role_name: string;
    }>;
}

interface AddResponseDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    reviewEntityId: number;
    reviewEntityName: string;
    entityReviewerIds: number[];
    entityRevieweeIds: number[];
}

export const AddResponseDialog: React.FC<AddResponseDialogProps> = ({ 
    open, 
    onClose, 
    onSuccess,
    reviewEntityId,
    reviewEntityName,
    entityReviewerIds,
    entityRevieweeIds
}) => {
    const [reviewName, setReviewName] = useState('');
    const [reviewDefinitionId, setReviewDefinitionId] = useState<number>(0);
    const [reviewHeader, setReviewHeader] = useState('');
    const [reviewComments, setReviewComments] = useState('');
    const [reviewerId, setReviewerId] = useState<number>(0);
    const [revieweeId, setRevieweeId] = useState<number>(0);
    const [reviewDate, setReviewDate] = useState(new Date().toISOString().split('T')[0]);
    const [reviewStartTime, setReviewStartTime] = useState('');
    const [users, setUsers] = useState<User[]>([]);
    const [reviewDefinitions, setReviewDefinitions] = useState<ReviewDefinition[]>([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [usersResponse, definitionsResponse] = await Promise.all([
                    getUsers(),
                    getReviewDefinitions()
                ]);
                setUsers(usersResponse.data);
                setReviewDefinitions(definitionsResponse.data);
            } catch (err) {
                setError('Failed to load form data');
                console.error('Error fetching form data:', err);
            }
        };

        if (open) {
            fetchData();
        } else {
            // Reset form when dialog closes
            setReviewName('');
            setReviewDefinitionId(0);
            setReviewHeader('');
            setReviewComments('');
            setReviewerId(0);
            setRevieweeId(0);
            setReviewDate(new Date().toISOString().split('T')[0]);
            setReviewStartTime('');
            setError('');
        }
    }, [open]);

    // Auto-populate reviewer and reviewee when there's only one option
    useEffect(() => {
        if (users.length > 0) {
            const reviewers = users.filter(user => 
                entityReviewerIds.includes(user.id)
            );
            
            const reviewees = users.filter(user => 
                entityRevieweeIds.includes(user.id)
            );

            // Auto-populate reviewer if only one option
            if (reviewers.length === 1 && reviewerId === 0) {
                setReviewerId(reviewers[0].id);
            }

            // Auto-populate reviewee if only one option
            if (reviewees.length === 1 && revieweeId === 0) {
                setRevieweeId(reviewees[0].id);
            }
        }
    }, [users, entityReviewerIds, entityRevieweeIds, reviewerId, revieweeId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const payload: QuestionnaireResponsePayload = {
                review_name: reviewName,
                review_definition_id: reviewDefinitionId,
                review_header: reviewHeader,
                review_comments: reviewComments,
                reviewer_id: reviewerId,
                reviewee_id: revieweeId,
                review_date: formatDateForAPI(reviewDate),
                review_entity_id: reviewEntityId,
                ...(reviewStartTime.trim()
                    ? { review_start_time: reviewStartTime.trim() }
                    : {})
            };

            await createQuestionnaireResponse(payload);
            onSuccess();
            onClose();
        } catch (err) {
            setError('Failed to create questionnaire response');
            console.error('Error creating response:', err);
        } finally {
            setLoading(false);
        }
    };

    const reviewers = users.filter(user => 
        entityReviewerIds.includes(user.id)
    );

    const reviewees = users.filter(user => 
        entityRevieweeIds.includes(user.id)
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Start New Review</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    {error && (
                        <Typography color="error" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Review Name"
                            value={reviewName}
                            onChange={(e) => setReviewName(e.target.value)}
                            required
                            fullWidth
                        />
                        <FormControl fullWidth required>
                            <InputLabel>Review Definition</InputLabel>
                            <Select
                                value={reviewDefinitionId || ''}
                                onChange={(e) => setReviewDefinitionId(Number(e.target.value))}
                                label="Review Definition"
                            >
                                {reviewDefinitions.map((def) => (
                                    <MenuItem key={def.id} value={def.id}>
                                        {def.display_label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Review Description"
                            value={reviewHeader}
                            onChange={(e) => setReviewHeader(e.target.value)}
                            required
                            fullWidth
                        />
                        <TextField
                            label="Review Comments"
                            value={reviewComments}
                            onChange={(e) => setReviewComments(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                        />
                        <FormControl fullWidth required>
                            <InputLabel>Reviewer</InputLabel>
                            <Select
                                value={reviewerId || ''}
                                onChange={(e) => setReviewerId(Number(e.target.value))}
                                label="Reviewer"
                            >
                                {reviewers.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.display_name || user.user_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth required>
                            <InputLabel>Reviewee</InputLabel>
                            <Select
                                value={revieweeId || ''}
                                onChange={(e) => setRevieweeId(Number(e.target.value))}
                                label="Reviewee"
                            >
                                {reviewees.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.display_name || user.user_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <InlineDatePicker
                            label="Review Date"
                            value={reviewDate}
                            onChange={(e) => setReviewDate(e.target.value)}
                            required
                            fullWidth
                        />
                        <TextField
                            type="time"
                            label="Start time (optional)"
                            value={reviewStartTime}
                            onChange={(e) => setReviewStartTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            helperText="If set, stored as review start time; otherwise the server uses the current time."
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        color="primary"
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

const QuestionnaireResponses: React.FC = () => {
    const [responses, setResponses] = useState<QuestionnaireResponse[]>([]);
    const [error, setError] = useState<string>('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isUserReviewer, setIsUserReviewer] = useState<boolean>(false);
    const [canCreateReview, setCanCreateReview] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [entityReviewers, setEntityReviewers] = useState<number[]>([]);
    const [entityReviewees, setEntityReviewees] = useState<number[]>([]);
    const navigate = useNavigate();
    const { entityId, entityName } = useParams();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // Get questionnaire responses for this entity
                const response = await getQuestionnaireResponsesByReviewEntity(Number(entityId));
                setResponses(response.data);
                
                // Check if current user is a reviewer for this entity
                const currentUser = await getUser();
                setCanCreateReview(UserUtils.canCreateReview(currentUser));
                if (entityId && currentUser) {
                    console.log('DEBUG: Checking if user is reviewer for entity', entityId);
                    try {
                        const entityResponse = await getReviewEntity(Number(entityId));
                        const entity = entityResponse.data;
                        console.log('DEBUG: Entity data:', entity);
                        console.log('DEBUG: Entity reviewers:', entity.reviewers);
                        console.log('DEBUG: Current user ID:', currentUser.id);
                        
                        // Store entity reviewers and reviewees for the dialog
                        setEntityReviewers(entity.reviewers || []);
                        setEntityReviewees(entity.reviewees || []);
                        
                        // Check if current user is in reviewers array
                        const isReviewer = entity.reviewers?.includes(currentUser.id);
                        console.log('DEBUG: User is reviewer:', isReviewer);
                        setIsUserReviewer(Boolean(isReviewer));
                    } catch (entityErr) {
                        console.error('ERROR: Failed to check if user is a reviewer:', entityErr);
                    }
                }
            } catch (err) {
                setError('Failed to load questionnaire responses');
                console.error('Error fetching responses:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [entityId]);

    const handleBack = () => {
        navigate('/review-management');
    };

    const renderReviewStatus = (response: QuestionnaireResponse) => {
        const overdueText = response.is_submission_overdue
            ? `Submission overdue${response.days_past_submission_due != null ? ` by ${response.days_past_submission_due}d` : ''}`
            : response.is_closure_overdue
                ? `Closure overdue${response.days_past_closure_due != null ? ` by ${response.days_past_closure_due}d` : ''}`
                : '';
        const overdueDueDate = response.is_submission_overdue
            ? response.submission_due_by
            : response.is_closure_overdue
                ? response.closure_due_by
                : null;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Chip label={response.review_status} size="small" />
                {response.is_overdue && (
                    <Chip
                        label={overdueText}
                        color="error"
                        size="small"
                        variant="outlined"
                    />
                )}
                {response.is_overdue && overdueDueDate && (
                    <Typography variant="caption" color="error">
                        Due by {formatDisplayDate(overdueDueDate)}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <>
            <TitleBar />
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBack />}
                                onClick={handleBack}
                            >
                                Back
                            </Button>
                            <Typography component="h2" variant="h6" color="primary">
                                Review details: {entityName}
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Add />}
                            onClick={() => setDialogOpen(true)}
                            disabled={isUserReviewer || !canCreateReview || loading}
                            title={
                                isUserReviewer
                                    ? 'Reviewers cannot add responses'
                                    : !canCreateReview
                                        ? 'Data entry support cannot create new reviews'
                                        : 'Add a new response'
                            }
                        >
                            Add Response
                        </Button>
                    </Box>

                    {error && (
                        <Typography color="error" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Review Name</TableCell>
                                    <TableCell>Review Definition</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Reviewer</TableCell>
                                    <TableCell>Reviewee</TableCell>
                                    <TableCell>Review Date</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Comments</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {responses.map((response) => (
                                    <TableRow key={response.id}>
                                        <TableCell>{response.review_name}</TableCell>
                                        <TableCell>
                                            {response.review_definition?.display_label || '-'}
                                        </TableCell>
                                        <TableCell>{response.review_header}</TableCell>
                                        <TableCell>{response.reviewer_name}</TableCell>
                                        <TableCell>{response.reviewee_name}</TableCell>
                                        <TableCell>{formatDisplayDate(response.review_date)}</TableCell>
                                        <TableCell>{renderReviewStatus(response)}</TableCell>
                                        <TableCell>{response.review_comments}</TableCell>
                                        <TableCell>
                                            <Tooltip title={isUserReviewer || response.review_status === 'SUBMITTED' ? 'View Answers' : 'View/Add Answers'}>
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => navigate(`/review-answers/${response.id}/${response.review_entity_type_id}`)}
                                                    size="medium"
                                                >
                                                    <Assignment />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Container>

            {entityId && entityName && (
                <AddResponseDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onSuccess={() => {
                        setDialogOpen(false);
                        // Refresh the responses list
                        const fetchResponses = async () => {
                            try {
                                const response = await getQuestionnaireResponsesByReviewEntity(Number(entityId));
                                setResponses(response.data);
                            } catch (err) {
                                setError('Failed to refresh questionnaire responses');
                                console.error('Error refreshing responses:', err);
                            }
                        };
                        fetchResponses();
                    }}
                    reviewEntityId={Number(entityId)}
                    reviewEntityName={entityName}
                    entityReviewerIds={entityReviewers}
                    entityRevieweeIds={entityReviewees}
                />
            )}
        </>
    );
};

export { QuestionnaireResponses };